import { useState } from 'react';

import { ContractFormActions } from './ContractFormActions';
import { ConfirmActionModal } from './ConfirmActionModal';
import { useContractData } from '../context';

import TextField from '@/components/TextField';
import { DetailItem, DetailsList } from '@/components/DetailsList';
import { FieldErrors } from '@/pages/application/types';
import { HelpDrawerLink } from '@/components/HelpDrawerLink';
import { RequiredFieldsLegend } from '@/components/RequiredFieldsLegend';
import { ValidationAnnouncement } from '@/components/ValidationAnnouncement';
import { useValidationAnnouncement } from '@/hooks/useValidationAnnouncement';
import { Drawer } from '@/components/Drawer';
import {
  email,
  hasErrors,
  maxLength,
  pattern,
  required,
  validateFields,
} from '@/pages/application/utils/fieldValidation';
import { CONTRACT_ROLE, useRBAC } from '@/hooks/useRBAC';
import { RosterOwnerFormData as FormData } from '../types';

interface RosterOwnersFormProps {
  initialValues: FormData;
  formData: FormData;
  hasEmailChanges: boolean;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  formErrors: FieldErrors;
  setFormErrors: React.Dispatch<React.SetStateAction<FieldErrors>>;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  'data-testid'?: string;
}

function RosterOwnersForm({
  initialValues,
  formData,
  hasEmailChanges,
  setFormData,
  formErrors,
  setFormErrors,
  onSubmit,
  isSubmitting,
  'data-testid': dataTestId,
}: RosterOwnersFormProps) {
  const baseTestId = dataTestId ?? 'roster-owners-tab';

  const { contract } = useContractData();
  const { getRolesForContract, isAdmin } = useRBAC();

  /**
   * ACP-2242: mirrors the server rule on the two endpoints saving this form calls —
   * `POST /users` and `POST /contracts/:id/role-assignments` both admit only ADMIN and
   * PRIMARY COR. Previously any COR variant got the editable form and an ALTERNATE was
   * only stopped once they pressed Save, after a placeholder account had been created.
   */
  const canEditRosterOwners =
    isAdmin ||
    getRolesForContract(contract?.id ?? '').some(
      (role) => role.role_type === CONTRACT_ROLE.COR && role.role_variant === 'PRIMARY',
    );

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  /**
   * ACP-2092 (Section 508): Save used to set the error state and stop. The messages
   * rendered, but a screen reader announces nothing on its own, so the press read as
   * inert until the user wandered back onto a field. Announce the invalid fields and
   * move focus to the first.
   */
  const fieldErrorSignature = Object.entries(formErrors)
    .filter(([, message]) => Boolean(message))
    .map(([field]) => field)
    .join('|');

  const {
    announcement: errorAnnouncement,
    reportInvalidFields,
    scopeRef,
  } = useValidationAnnouncement<HTMLDivElement>({ errorSignature: fieldErrorSignature });

  const isDirty =
    hasEmailChanges ||
    formData.rosterOwnerName !== initialValues.rosterOwnerName ||
    formData.delegateOwnerName !== initialValues.delegateOwnerName;

  const handleConfirmDiscard = () => {
    setFormData(initialValues);
    setFormErrors({});
    setSuccessMessage('Changes discarded.');
  };

  const handleSave = () => {
    const config = {
      delegateOwnerEmail: [email, maxLength(255)],
      delegateOwnerName: [
        pattern(/^[a-zA-Z' -]+$/, 'Name can only contain letters, apostrophes, hyphens, and spaces.'),
        maxLength(100),
      ],
      rosterOwnerEmail: [required, email, maxLength(255)],
      rosterOwnerName: [
        required,
        pattern(/^[a-zA-Z' -]+$/, 'Name can only contain letters, apostrophes, hyphens, and spaces.'),
        maxLength(100),
      ],
    };

    const errors = validateFields(formData as unknown as Record<string, unknown>, config);

    if (formData.delegateOwnerName && !formData.delegateOwnerEmail) {
      errors.delegateOwnerEmail = 'Email is required when a delegate name is provided.';
    }

    if (formData.delegateOwnerEmail && !formData.delegateOwnerName) {
      errors.delegateOwnerName = 'Name is required when a delegate email is provided.';
    }

    setFormErrors(errors);

    if (hasErrors(errors)) {
      // Batched with setFormErrors, so the effect sees the controls already marked invalid.
      reportInvalidFields();

      return;
    }

    setSuccessMessage('');
    setIsConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    await onSubmit();
    setSuccessMessage('Roster Owner data saved successfully.');
  };

  const helpDrawer = (
    <Drawer
      data-testid={`${baseTestId}-drawer`}
      heading='Help: Roster Owners'
      isOpen={isHelpOpen}
      width='50%'
      onClose={() => setIsHelpOpen(false)}>
      <h3>Roster Owner Name</h3>
      <p>
        The legal first and last name of someone who will act as the Roster Owner for this Contract. This person must
        also work for the company that will be working on this Contract.
      </p>
      <h3>Roster Owner Email</h3>
      <p>
        The email address of someone who will act as the Roster Owner for this Contract. This person must also work for
        the company that will be working on this Contract. If this person has been assigned a CMS email address, that is
        the address to use. Otherwise, another email address may be used. This email address will need to be used
        throughout CMS systems for this person.
      </p>
      <h3>Delegate Roster Owner Name</h3>
      <p>
        The legal first and last name of someone who will act as the Delegate Roster Owner for this Contract while the
        regular Roster Owner is out of office. This person must also work for the company that will be working on this
        Contract.
      </p>
      <h3>Delegate Roster Owner Email</h3>
      <p>
        The email address of someone who will act as the Delegate Roster Owner for this Contract while the regular
        Roster Owner is out of office. This person must also work for the company that will be working on this Contract.
        If this person has been assigned a CMS email address, that is the address to use. Otherwise, another email
        address may be used. This email address will need to be used throughout CMS systems for this person.
      </p>
    </Drawer>
  );

  if (!canEditRosterOwners) {
    const readOnlyItems: DetailItem[] = [
      { label: 'Roster Owner Name', value: initialValues.rosterOwnerName || '—' },
      { label: 'Roster Owner Email', value: initialValues.rosterOwnerEmail || '—' },
      { label: 'Delegate Roster Owner Name', value: initialValues.delegateOwnerName || '—' },
      { label: 'Delegate Roster Owner Email', value: initialValues.delegateOwnerEmail || '—' },
    ];

    return (
      <div
        className='roster-owners-tab-content'
        data-testid={baseTestId}>
        <div
          className='roster-owners-tab__content-container'
          data-testid={`${baseTestId}-content`}>
          <p
            className='ds-u-margin-bottom--0'
            data-testid={`${baseTestId}-intro`}>
            If any of the information in this section is incorrect, please work with your Contracting Officer to update.
          </p>

          <DetailsList
            data-testid={`${baseTestId}-details`}
            header='Roster Owners'
            items={readOnlyItems}
          />

          <div className='roster-owners-tab__actions-wrapper'>
            <HelpDrawerLink
              className='ds-u-margin-top--2'
              data-testid={`${baseTestId}-help-link`}
              onClick={() => setIsHelpOpen(true)}
            />
            {helpDrawer}
            <ContractFormActions isEditable={false} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        ref={scopeRef}
        className='roster-owners-tab-content'
        data-testid={baseTestId}>
        <ValidationAnnouncement
          data-testid={`${baseTestId}-error-announcement`}
          message={errorAnnouncement}
        />
        <div
          className='roster-owners-tab__content-container'
          data-testid={`${baseTestId}-content`}>
          <div data-testid={`${baseTestId}-section-header`}>
            <p
              className='ds-u-margin-bottom--0'
              data-testid={`${baseTestId}-intro`}>
              If any of the information in this section is incorrect, please work with your Contracting Officer to have
              CALM updated.
            </p>
            <RequiredFieldsLegend
              className='ds-u-margin-top--0 ds-u-margin-bottom--2'
              data-testid={`${baseTestId}-required-fields`}
            />
            <h3
              className='ds-u-margin-top--0 ds-u-margin-bottom--1'
              data-testid={`${baseTestId}-section-title`}>
              Roster Owners
              <span
                aria-label='required'
                style={{ color: 'var(--color-error)' }}>
                *
              </span>
            </h3>
            <p
              className='ds-u-margin-bottom--0'
              data-testid={`${baseTestId}-section-hint`}>
              You must assign a Roster Owner.
            </p>
            <p
              className='ds-text ds-u-margin-bottom--2'
              data-testid={`${baseTestId}-section-hint-delegate`}>
              Assigning a Delegate Roster Owner is optional.
            </p>
          </div>
          <TextField
            required
            data-testid={`${baseTestId}-roster-owner-name`}
            errorMessage={
              formErrors.rosterOwnerName &&
              (formErrors.rosterOwnerName === 'This field is required'
                ? 'You must enter data.'
                : formErrors.rosterOwnerName)
            }
            label='Roster Owner Name'
            name='rosterOwnerName'
            tooltipText='Enter the name of the Roster Owner for this contract.'
            value={formData.rosterOwnerName}
            onChange={(e) => setFormData({ ...formData, rosterOwnerName: e.target.value })}
          />
          <TextField
            required
            data-testid={`${baseTestId}-roster-owner-email`}
            errorMessage={
              formErrors.rosterOwnerEmail &&
              (formErrors.rosterOwnerEmail === 'This field is required'
                ? 'You must enter data.'
                : formErrors.rosterOwnerEmail)
            }
            label='Roster Owner Email'
            name='rosterOwnerEmail'
            tooltipText='Enter the email address of the Roster Owner for this contract. Note that if the Roster Owner has been assigned a CMS email address, enter that address. Otherwise, another email address may be used. The email address entered here must be used throughout CMS systems for this person.'
            type='email'
            value={formData.rosterOwnerEmail}
            onChange={(e) => setFormData({ ...formData, rosterOwnerEmail: e.target.value })}
          />
          <TextField
            data-testid={`${baseTestId}-delegate-owner-name`}
            errorMessage={formErrors.delegateOwnerName}
            label='Delegate Roster Owner Name'
            name='delegateOwnerName'
            tooltipText='Enter the name of the Delegate Roster Owner for this contract if applicable.'
            value={formData.delegateOwnerName}
            onChange={(e) => setFormData({ ...formData, delegateOwnerName: e.target.value })}
          />
          <TextField
            data-testid={`${baseTestId}-delegate-owner-email`}
            errorMessage={formErrors.delegateOwnerEmail}
            label='Delegate Roster Owner Email'
            name='delegateOwnerEmail'
            tooltipText='Enter the email address of the Delegate Roster Owner for this contract if applicable. Note that if the Delegate Roster Owner has been assigned a CMS email address, enter that address. Otherwise, another email address may be used. The email address entered here must be used throughout CMS systems for this person.'
            type='email'
            value={formData.delegateOwnerEmail}
            onChange={(e) => setFormData({ ...formData, delegateOwnerEmail: e.target.value })}
          />

          <div className='roster-owners-tab__actions-wrapper'>
            <HelpDrawerLink
              className='ds-u-margin-top--2'
              data-testid={`${baseTestId}-help-link`}
              onClick={() => setIsHelpOpen(true)}
            />
            {helpDrawer}
            <ContractFormActions
              discard={{
                disabled: !isDirty || isSubmitting,
                onClick: () => {
                  setSuccessMessage('');
                  setIsDiscardConfirmOpen(true);
                },
              }}
              isEditable={canEditRosterOwners}
              save={{
                disabled: !isDirty || isSubmitting,
                onClick: handleSave,
              }}
            />
          </div>
        </div>
      </div>
      <ConfirmActionModal
        cancelLabel='Cancel'
        confirmLabel='Save'
        data-testid={`${baseTestId}-save-modal`}
        description='You have changed one or more fields for the Roster Owner data. Are you sure you want to save these changes?'
        isConfirming={isSubmitting}
        isOpen={isConfirmOpen}
        successMessage={successMessage}
        title='Save new Roster Owner data?'
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmSave}
      />
      <ConfirmActionModal
        cancelLabel='Cancel'
        confirmLabel='Confirm Discard'
        data-testid={`${baseTestId}-discard-modal`}
        description='Are you sure you want to discard all your changes? This action cannot be undone and the contract will revert to the last time this was saved.'
        isOpen={isDiscardConfirmOpen}
        successMessage={successMessage}
        title='Discard Changes?'
        onClose={() => setIsDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />
    </>
  );
}

export default RosterOwnersForm;
